/**
 * PhonemeChip — one sound in the Sound Map wrap-grid.
 *
 * IPA symbol + state color (server-computed `state`, never re-derived),
 * small avg score when tried, and a subtle "expected hard" badge on
 * untried sounds with contrastive difficulty >= 4.
 *
 * Rendered ~46 times per screen — React.memo'd, callbacks stable.
 *
 * @see docs/specs/mobile/sound-map.mobile.md
 */

import React, { useCallback } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { FS_XS, FS_LG } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import type { SoundMapPhoneme } from '../../generated/api/http-contracts';
import { chipA11yLabel, showsExpectedHardBadge, type PhonemeState } from './soundMapText';

interface PhonemeChipProps {
  phoneme: SoundMapPhoneme;
  onPress: (phoneme: SoundMapPhoneme) => void;
}

/** State → semantic theme colors (soft background + border accent). */
function useStateColors(state: PhonemeState) {
  const { theme } = useDisplayTheme();
  switch (state) {
    case 'mastered': return { bg: theme.successBg, border: theme.success, symbol: theme.text };
    case 'learning': return { bg: theme.warningBg, border: theme.warning, symbol: theme.text };
    case 'weak': return { bg: theme.errorBg, border: theme.error, symbol: theme.text };
    case 'untried': return { bg: theme.card, border: theme.border, symbol: theme.textSecondary };
  }
}

export const PhonemeChip = React.memo(function PhonemeChip({ phoneme, onPress }: PhonemeChipProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const stateColors = useStateColors(phoneme.state);

  const handlePress = useCallback(() => {
    onPress(phoneme);
  }, [onPress, phoneme]);

  const tried = phoneme.state !== 'untried' && phoneme.avgScore !== null;
  const showHardBadge = showsExpectedHardBadge(phoneme);

  return (
    <TouchableOpacity
      testID={`sound-map-chip-${phoneme.ipaSymbol}`}
      accessibilityRole="button"
      accessibilityLabel={chipA11yLabel(t, phoneme)}
      onPress={handlePress}
      activeOpacity={0.7}
      style={[styles.chip, { backgroundColor: stateColors.bg, borderColor: stateColors.border }]}
    >
      <Text
        allowFontScaling={false}
        style={{ fontSize: FS_LG * fontScale, fontWeight: '600', color: stateColors.symbol, ...ffs }}
      >
        {phoneme.ipaSymbol}
      </Text>
      {tried && (
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
        >
          {t('soundMap.chip.scoreValue', { score: phoneme.avgScore })}
        </Text>
      )}
      {showHardBadge && (
        <View
          testID={`sound-map-hard-${phoneme.ipaSymbol}`}
          accessible={false}
          importantForAccessibility="no-hide-descendants"
          style={[styles.hardBadge, { backgroundColor: theme.warning, borderColor: theme.card }]}
        >
          <Text allowFontScaling={false} style={styles.hardBadgeText}>
            !
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  chip: {
    minWidth: 56,
    minHeight: 56,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hardBadge: {
    position: 'absolute',
    top: -5,
    right: -5,
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hardBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
    lineHeight: 12,
  },
});
